import { InputNumber, Space, Typography } from 'antd'
import CIMSCollapse from 'components/CIMSCollapse'
import { ConcreteTestEditPanelCollapseProps } from './types'
import { IFIReportType } from '@cims-online/shared'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import CIMSTextArea from 'components/CIMSTextArea'
import CIMSSwitch from 'components/CIMSSwitch'
import CIMSRangeGroup from 'components/CIMSRangeGroup'
import { useWatch } from 'antd/lib/form/Form'
import { useEffect } from 'react'
import DirtyHeader from '../../../../../../components/DirtyHeader'

const Help = HelpMessage.Administration.ProjectSettings.IFITracker
const {
  type: reportType,
  defaultMessages: {
    ambient,
    highTemp,
    lowTemp,
    highSlump,
    lowSlump,
    highAir,
    lowAir,
  },
} = IFIReportType.ConcreteTest

const ConcreteTestEditPanelCollapse: React.VFC<
  ConcreteTestEditPanelCollapseProps
> = ({ form, dirtyPanels, openPanels, setOpenPanels }) => {
  const minAmbientAirWatch = useWatch('minAmbientAir', form)
  const temperatureWatch = useWatch('temperature', form)

  useEffect(() => {
    if (!minAmbientAirWatch) form.setFieldValue('ambientAirEnabled', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minAmbientAirWatch])

  useEffect(() => {
    if (
      typeof temperatureWatch?.max !== 'number' ||
      typeof temperatureWatch?.min !== 'number'
    )
      form.setFieldValue('temperatureEnabled', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temperatureWatch])

  return (
    <CIMSCollapse
      multiple
      activeKey={openPanels}
      onChange={setOpenPanels}
      panels={[
        {
          header: {
            children: (
              <DirtyHeader
                title="Ambient Air"
                isDirty={dirtyPanels.includes('ambient')}
              />
            ),
          },
          key: `edit-ambient-air-${IFIReportType.ConcreteTest.id}`,
          // Must force render panels for Switch disabled checks to work on load
          forceRender: true,
          children: (
            <>
              <Space direction="vertical" size="middle">
                <Typography>
                  If the Air Temp is less than the Minimum Ambient Air
                  Temperature, issue an Identified Field Issue using the
                  Message.
                </Typography>
                <CIMSFormItem
                  name="minAmbientAir"
                  label="Minimum Ambient Air Temperature"
                  help={Help.MinAmbientTemp}
                >
                  <InputNumber />
                </CIMSFormItem>
              </Space>
              <CIMSFormItem
                name="minAmbientAirMessage"
                label="Message"
                help={Help.FailureMessage()}
                defaultMessage={ambient}
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="ambientAirEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch
                  disabled={{
                    disabled: typeof minAmbientAirWatch !== 'number',
                    reason: {
                      why: 'disabled',
                      message:
                        'Cannot be enabled without setting a Minimum Ambient Air Temperature',
                    },
                  }}
                  onClick={(_, event) => event.stopPropagation()}
                />
              </CIMSFormItem>
            ),
          },
        },
        {
          header: {
            children: (
              <DirtyHeader
                title="Temperature"
                isDirty={dirtyPanels.includes('temperature')}
              />
            ),
          },
          key: `edit-temperature-${IFIReportType.ConcreteTest.id}`,
          forceRender: true,
          children: (
            <>
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Concrete Temp to the Concrete Temperature Range to
                  determine whether to issue an Identified Field Issue. If less
                  than, use the Low Temperature Message. If greater than, use
                  the High Temperature Message. If there is no Concrete Temp,
                  issue an Identified Field Issue with the message "No Concrete
                  Temp".
                </Typography>
                <CIMSRangeGroup
                  name="temperature"
                  label="Concrete Temperature Range"
                  help={Help.TempRange}
                />
              </Space>
              <Space
                direction="vertical"
                size="small"
                style={{ width: '100%' }}
              >
                <CIMSFormItem
                  name="highTempMessage"
                  label="High Temperature Message"
                  help={Help.FailureMessage({
                    reportType,
                    reason: 'above the High Temperature limit',
                  })}
                  defaultMessage={highTemp}
                >
                  <CIMSTextArea />
                </CIMSFormItem>
                <CIMSFormItem
                  name="lowTempMessage"
                  label="Low Temperature Message"
                  help={Help.FailureMessage({
                    reportType,
                    reason: 'below the Low Temperature limit',
                  })}
                  defaultMessage={lowTemp}
                >
                  <CIMSTextArea />
                </CIMSFormItem>
              </Space>
            </>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="temperatureEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch
                  disabled={{
                    disabled:
                      typeof temperatureWatch?.max !== 'number' ||
                      typeof temperatureWatch?.min !== 'number',
                    reason: {
                      why: 'disabled',
                      message:
                        'Cannot be enabled without setting a Concrete Temperature Range',
                    },
                  }}
                  onClick={(_, event) => event.stopPropagation()}
                />
              </CIMSFormItem>
            ),
          },
        },
        {
          header: {
            children: (
              <DirtyHeader
                title="Slump"
                isDirty={dirtyPanels.includes('slump')}
              />
            ),
          },
          key: `edit-slump-${IFIReportType.ConcreteTest.id}`,
          forceRender: true,
          children: (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Slump to the Slump Range of the Concrete Mix
                  Design to determine whether to issue an Identified Field
                  Issue. If less than, use the Low Slump Message. If greater,
                  use the High Slump Message. If there is no Slump, issue an
                  Identified Field Issue with the message "No Slump".
                </Typography>
                <CIMSFormItem
                  name="highSlumpMessage"
                  label="High Slump Message"
                  help={Help.FailureMessage({
                    reportType,
                    reason: 'above the maximum allowed Slump',
                  })}
                  defaultMessage={highSlump}
                >
                  <CIMSTextArea />
                </CIMSFormItem>
              </Space>
              <CIMSFormItem
                name="lowSlumpMessage"
                label="Low Slump Message"
                help={Help.FailureMessage({
                  reportType,
                  reason: 'below the minimum allowed Slump',
                })}
                defaultMessage={lowSlump}
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </Space>
          ),
          extra: {
            children: (
              <CIMSFormItem noStyle name="slumpEnabled" valuePropName="checked">
                <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
              </CIMSFormItem>
            ),
          },
        },
        {
          header: {
            children: (
              <DirtyHeader
                title="Percent Air"
                isDirty={dirtyPanels.includes('percent')}
              />
            ),
          },
          key: `edit-percent-air-${IFIReportType.ConcreteTest.id}`,
          forceRender: true,
          children: (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Percent Air to the Air Range of the Concrete Mix
                  Design to determine whether to issue an Identified FIeld
                  Issue. If less than, use the Low Air Message. If greater than,
                  use the High Air Message. If there is no Percent Air, issue an
                  Identified Field Issue with the message "No Percent Air".
                </Typography>
                <CIMSFormItem
                  name="highAirMessage"
                  label="High Air Message"
                  help={Help.FailureMessage({
                    reportType,
                    reason: 'above the high Percent Air limit',
                  })}
                  defaultMessage={highAir}
                >
                  <CIMSTextArea />
                </CIMSFormItem>
              </Space>
              <CIMSFormItem
                name="lowAirMessage"
                label="Low Air Message"
                help={Help.FailureMessage({
                  reportType,
                  reason: 'below the low Percent Air limit',
                })}
                defaultMessage={lowAir}
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </Space>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="percentAirEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
              </CIMSFormItem>
            ),
          },
        },
      ]}
    />
  )
}

export default ConcreteTestEditPanelCollapse
